import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Tab,
	Tabs,
	TextField,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import {
	TPgAdminPaymentMethod,
	TPgCustomer,
	TPgPaymentListItem,
	TPgSyncConsentsResult,
	TPgUkOverride,
} from 'services/api/paymentGateway/types';
import { ESortDirection } from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgFields } from '../common/PgFields';
import { PgMiniTable, PgStatusChip } from '../common/PgMiniTable';
import { humanize } from '../common/tableHelpers';
import { PG_METHOD_TYPE_LABELS, PG_UK_OVERRIDE_LABELS } from '../constants';
import { ChargeInvoiceDialog } from '../Payments/ChargeInvoiceDialog';
import { usePaymentViewer } from '../Payments/PaymentViewer';
import { formatDateTime, formatMoney } from '../utils';
import { ChargeCustomerDialog } from './ChargeCustomerDialog';
import { CustomerLegacyTransactions } from './LegacyTransactions';
import { MethodServicesDialog } from './MethodServicesDialog';

type TCustomerDialogProps = {
	/** `null` — closed. */
	customerId: number | null;
	onClose: () => void;
	/** The customer was changed: the list should re-read. */
	onChanged?: () => void;
};

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
	<Box sx={{ mt: 2 }}>
		<Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
			{title}
		</Typography>
		{children}
	</Box>
);

const RECENT_PAYMENTS = 10;

type TCustomerTab = 'overview' | 'legacy';

/**
 * Customer card (03 §4.7): payment methods with their services, coverage, the UK override and the
 * actions on the customer — refresh from EAS, consent sync, manual charges.
 */
export const CustomerDialog: FC<TCustomerDialogProps> = ({ customerId, onClose, onChanged }) => {
	const { openPayment } = usePaymentViewer();
	const [customer, setCustomer] = useState<TPgCustomer | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [acting, setActing] = useState<string | null>(null);
	const [ukOverride, setUkOverride] = useState<TPgUkOverride>('auto');
	const [syncResult, setSyncResult] = useState<TPgSyncConsentsResult | null>(null);
	const [payments, setPayments] = useState<TPgPaymentListItem[]>([]);
	const [servicesMethod, setServicesMethod] = useState<TPgAdminPaymentMethod | null>(null);
	const [disableMethod, setDisableMethod] = useState<TPgAdminPaymentMethod | null>(null);
	const [disableBusy, setDisableBusy] = useState(false);
	const [disableError, setDisableError] = useState<string | null>(null);
	const [chargeInvoiceOpen, setChargeInvoiceOpen] = useState(false);
	const [chargeAllOpen, setChargeAllOpen] = useState(false);
	// The tab belongs to the customer it was chosen for: another customer opens on the overview.
	const [tabState, setTabState] = useState<{ customerId: number | null; tab: TCustomerTab }>({
		customerId: null,
		tab: 'overview',
	});
	const tab: TCustomerTab = tabState.customerId === customerId ? tabState.tab : 'overview';

	const close = () => {
		setTabState({ customerId: null, tab: 'overview' });
		onClose();
	};

	const apply = useCallback((next: TPgCustomer) => {
		setCustomer(next);
		setUkOverride(next.ukOverride);
	}, []);

	const loadPayments = useCallback(async (id: number) => {
		try {
			const page = await api.paymentGateway.getPayments({
				pageNumber: 1,
				pageSize: RECENT_PAYMENTS,
				searchString: '',
				filters: [
					{ fieldName: 'customerId', name: 'customerId', value: String(id), operator: '=' },
				],
				orderBy: [{ fieldName: 'createdAt', direction: ESortDirection.DESC }],
			});
			setPayments(page.data);
		} catch {
			setPayments([]);
		}
	}, []);

	const load = useCallback(
		async (id: number) => {
			setLoading(true);
			setError(null);
			try {
				apply(await api.paymentGateway.getCustomer(id));
			} catch (e) {
				setError(getPgErrorMessage(e, 'Failed to load the customer'));
			} finally {
				setLoading(false);
			}
			void loadPayments(id);
		},
		[apply, loadPayments],
	);

	useEffect(() => {
		setCustomer(null);
		setError(null);
		setSyncResult(null);
		setPayments([]);
		if (customerId) void load(customerId);
	}, [customerId, load]);

	const act = async (name: string, run: () => Promise<void>, failure: string) => {
		setActing(name);
		try {
			await run();
		} catch (e) {
			notificationContainer(getPgErrorMessage(e, failure), 'error');
		} finally {
			setActing(null);
		}
	};

	const saveUkOverride = () =>
		customer &&
		act(
			'uk',
			async () => {
				apply(
					await api.paymentGateway.updateCustomerUkOverride({
						id: customer.id,
						body: { ukOverride },
					}),
				);
				notificationContainer('UK override saved', 'success');
				onChanged?.();
			},
			'Failed to save the UK override',
		);

	const refresh = () =>
		customer &&
		act(
			'refresh',
			async () => {
				apply(await api.paymentGateway.refreshCustomer(customer.id));
				notificationContainer('Customer re-read from EAS', 'success');
				onChanged?.();
			},
			'Failed to refresh the customer',
		);

	const syncConsents = () =>
		customer &&
		act(
			'consents',
			async () => {
				const result = await api.paymentGateway.syncCustomerConsents(customer.id);
				setSyncResult(result);
				setCustomer((prev) => (prev ? { ...prev, paymentMethods: result.paymentMethods } : prev));
				notificationContainer('Consents compared with Airwallex', 'success');
			},
			'Failed to sync the consents',
		);

	const disable = async (reason: string) => {
		if (!disableMethod || !customer) return;
		setDisableBusy(true);
		setDisableError(null);
		try {
			await api.paymentGateway.disablePaymentMethod({ id: disableMethod.id, body: { reason } });
			notificationContainer('Payment method disabled', 'success');
			setDisableMethod(null);
			await load(customer.id);
			onChanged?.();
		} catch (e) {
			setDisableError(getPgErrorMessage(e, 'Failed to disable the payment method'));
		} finally {
			setDisableBusy(false);
		}
	};

	const c = customer;
	const methods = c?.paymentMethods ?? [];
	const canDisable = (method: TPgAdminPaymentMethod) =>
		method.status === 'active' || method.status === 'pending';

	return (
		<>
			<Dialog open={!!customerId} onClose={close} maxWidth="lg" fullWidth scroll="paper">
				<DialogTitle
					color="primary"
					sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
				>
					{c ? `${c.clientNumber}${c.displayName ? ` — ${c.displayName}` : ''}` : 'Customer'}
					{c?.isUk && <Chip size="small" label="UK" color="info" sx={{ height: 20 }} />}
					{c?.needsRelink && (
						<Chip size="small" label="Needs re-link" color="warning" sx={{ height: 20 }} />
					)}
				</DialogTitle>
				{c && (
					<Tabs
						value={tab}
						onChange={(_event, value: TCustomerTab) => setTabState({ customerId, tab: value })}
						sx={{ px: 3, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
					>
						<Tab value="overview" label="Overview" />
						<Tab value="legacy" label="Previous payment system" />
					</Tabs>
				)}
				<DialogContent dividers>
					{loading && !c && <CircularProgress size={24} />}
					{error && <Alert severity="error">{error}</Alert>}
					{c && tab === 'legacy' && <CustomerLegacyTransactions key={c.id} customerId={c.id} />}
					{c && tab === 'overview' && (
						<>
							<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
								<Button
									size="small"
									variant="outlined"
									onClick={() => void refresh()}
									disabled={!!acting}
									startIcon={acting === 'refresh' ? <CircularProgress size={14} /> : undefined}
								>
									Refresh from EAS
								</Button>
								<Button
									size="small"
									variant="outlined"
									onClick={() => void syncConsents()}
									disabled={!!acting}
									startIcon={acting === 'consents' ? <CircularProgress size={14} /> : undefined}
								>
									Sync consents
								</Button>
								<Button
									size="small"
									variant="contained"
									color="error"
									onClick={() => setChargeInvoiceOpen(true)}
									disabled={!!acting}
								>
									Charge invoice
								</Button>
								<Button
									size="small"
									variant="contained"
									color="error"
									onClick={() => setChargeAllOpen(true)}
									disabled={!!acting}
								>
									Charge all open invoices
								</Button>
							</Box>

							<PgFields
								items={[
									['EM ID', c.emId],
									['Registered country', c.registeredCountryCode],
									['Treated as UK', c.isUk ? 'Yes' : 'No'],
									['EAS enabled', c.easEnabled === null ? null : c.easEnabled ? 'Yes' : 'No'],
									['Re-read from EAS', formatDateTime(c.easRefreshedAt)],
									['In the gateway since', formatDateTime(c.createdAt)],
								]}
							/>
							<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
								<TextField
									select
									size="small"
									label="UK override (Direct Debit)"
									value={ukOverride}
									onChange={(e) => setUkOverride(e.target.value as TPgUkOverride)}
									sx={{ minWidth: 260 }}
									disabled={acting === 'uk'}
								>
									{Object.entries(PG_UK_OVERRIDE_LABELS).map(([value, label]) => (
										<MenuItem key={value} value={value}>
											{label}
										</MenuItem>
									))}
								</TextField>
								<Button
									size="small"
									variant="contained"
									onClick={() => void saveUkOverride()}
									disabled={ukOverride === c.ukOverride || acting === 'uk'}
								>
									Save
								</Button>
							</Box>

							{syncResult && (
								<Alert
									severity={syncResult.errors || syncResult.orphans.length ? 'warning' : 'success'}
									sx={{ mt: 2 }}
								>
									Consents checked: {syncResult.checked}, changed: {syncResult.changed}, errors:{' '}
									{syncResult.errors}.
									{syncResult.orphans.length > 0 &&
										` Consents at Airwallex without a payment method here: ${syncResult.orphans
											.map((orphan) => `${orphan.awxConsentId} (${orphan.status})`)
											.join(', ')}.`}
								</Alert>
							)}

							<Section title={`Payment methods (${methods.length})`}>
								<PgMiniTable
									rows={methods}
									getKey={(row) => row.id}
									emptyText="The customer has no payment methods"
									columns={[
										{
											header: 'Method',
											render: (row) => (
												<>
													{row.label}
													<Typography variant="caption" color="text.secondary" component="div">
														{PG_METHOD_TYPE_LABELS[row.type]}
														{row.isDefault ? ' · default' : ''}
														{row.expiringSoon ? ' · expires soon' : ''}
														{row.isExpired ? ' · expired' : ''}
													</Typography>
												</>
											),
										},
										{ header: 'Status', render: (row) => <PgStatusChip value={row.status} /> },
										{
											header: 'Services',
											render: (row) =>
												row.allServices ? (
													'All services'
												) : (
													<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
														{row.services.map((service) => (
															<Chip key={service.id} size="small" label={service.name} />
														))}
													</Box>
												),
										},
										{ header: 'Source', render: (row) => humanize(row.source) },
										{ header: 'Last used', render: (row) => formatDateTime(row.lastUsedAt) },
										{
											header: 'Consent',
											render: (row) => (
												<Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
													{row.awxConsentId}
													{row.disableReason ? ` — ${row.disableReason}` : ''}
												</Typography>
											),
										},
										{
											header: '',
											render: (row) => (
												<Box sx={{ display: 'flex', gap: 0.5 }}>
													<Button
														size="small"
														onClick={() => setServicesMethod(row)}
														disabled={!canDisable(row)}
													>
														Services
													</Button>
													<Button
														size="small"
														color="error"
														onClick={() => {
															setDisableError(null);
															setDisableMethod(row);
														}}
														disabled={!canDisable(row)}
													>
														Disable
													</Button>
												</Box>
											),
										},
									]}
								/>
							</Section>

							<Section title="Coverage: who pays for each service">
								<PgMiniTable
									rows={c.coverage}
									getKey={(row) => row.service.id}
									columns={[
										{ header: 'Service', render: (row) => row.service.name },
										{
											header: 'Paid by',
											render: (row) =>
												row.covered && row.primary ? (
													`${row.primary.label} (${PG_METHOD_TYPE_LABELS[row.primary.type]})`
												) : (
													<PgStatusChip value="no_method" label="Not covered" />
												),
										},
										{
											header: 'Fallbacks',
											align: 'right',
											render: (row) => row.fallbackPaymentMethodIds.length,
										},
									]}
								/>
							</Section>

							<Section title="Airwallex customers">
								<PgMiniTable
									rows={c.providerCustomers}
									getKey={(row) => `${row.accountCode}:${row.awxCustomerId}`}
									columns={[
										{ header: 'Account', render: (row) => row.accountCode },
										{ header: 'Airwallex customer', render: (row) => row.awxCustomerId },
										{ header: 'Primary', render: (row) => (row.isPrimary ? 'Yes' : 'No') },
										{ header: 'Source', render: (row) => row.source },
										{ header: 'Created', render: (row) => formatDateTime(row.createdAt) },
									]}
								/>
							</Section>

							<Section title={`Latest payments (${payments.length})`}>
								<PgMiniTable
									rows={payments}
									getKey={(row) => row.id}
									onRowClick={(row) => openPayment(row.transactionId)}
									columns={[
										{ header: 'Created', render: (row) => formatDateTime(row.createdAt) },
										{ header: 'Status', render: (row) => <PgStatusChip value={row.status} /> },
										{ header: 'Trigger', render: (row) => humanize(row.trigger) },
										{ header: 'Invoice', render: (row) => row.invoiceNumber ?? '—' },
										{ header: 'Service', render: (row) => row.serviceCode ?? '—' },
										{
											header: 'Total',
											align: 'right',
											render: (row) => formatMoney(row.total, row.currency),
										},
									]}
								/>
							</Section>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={close}>Close</Button>
				</DialogActions>
			</Dialog>

			<MethodServicesDialog
				method={servicesMethod}
				onClose={() => setServicesMethod(null)}
				onDone={() => {
					setServicesMethod(null);
					if (customerId) void load(customerId);
				}}
			/>
			<PgConfirmDialog
				open={!!disableMethod}
				title="Disable payment method"
				confirmLabel="Disable"
				confirmColor="error"
				reasonLabel="Reason (shown in the audit log)"
				busy={disableBusy}
				error={disableError}
				onConfirm={(reason) => void disable(reason)}
				onClose={() => setDisableMethod(null)}
				description={`This disables the consent of ${disableMethod?.label ?? 'the payment method'} at Airwallex. The customer's invoices can no longer be charged with it, and it cannot be enabled again: the customer has to add it anew.`}
			/>
			{c && (
				<>
					<ChargeInvoiceDialog
						open={chargeInvoiceOpen}
						onClose={() => setChargeInvoiceOpen(false)}
						methods={methods}
						onDone={() => void load(c.id)}
					/>
					<ChargeCustomerDialog
						open={chargeAllOpen}
						customer={c}
						onClose={() => setChargeAllOpen(false)}
					/>
				</>
			)}
		</>
	);
};
